/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useCallback } from 'react';
import { Container, Typography, Button, Table, Badge, useToast } from '@crisellevaldez/speculo-ui';
import { SteppedModal } from '../../components/common/SteppedModal';
import { EditFlowModal } from '../../components/features/flows/EditFlowModal';
import { useUser } from '../../hooks/useUser';
import { ViewFlowContactsModal } from '../../components/features/flows/ViewFlowContactsModal';
import { FlowCallRecordsModal } from '../../components/features/flows/FlowCallRecordsModal';
import {
  PlusCircle,
  Phone,
  UserPlus,
  Pencil,
  Trash,
  RotateCw,
  Megaphone,
  Upload,
} from 'lucide-react';
import { ConfirmationDialog, LoadingDialog } from '../../components/common/dialogs';
import { Flow } from '../../hooks/useFlowsQuery';
import { useFlowsGQL } from '../../hooks/graphql/flows/useFlowsGQL';
import { OffsetPagination } from '../../components/common/OffsetPagination';
import { TableCard } from '../../components/common/TableCard';
import { useOrganizationStore } from '../../stores/useOrganizationStore';
import { useHasIntegration } from '../../hooks/useHasIntegration';
import { useOrganizationUsers } from '../../hooks/useOrganizationUsers';
import { formatNumber } from '../../utils/format';
import moment from 'moment-timezone';
import { getFlowStatusStyle, getFlowSubTypeStyle } from '../../utils/badgeStyles';
import { capitalizeFirst } from '../../utils/strings';
import { EngageFlowSteps } from '../../components/features/flows/EngageFlowSteps';
import { createFlow } from '../../services/flows';
import { generateVoicemail as generateVoicemailAPI } from '../../api/flows';
import { deleteFlow } from '../../api/remiApi';
import { AddContactsModal } from '../../components/features/flows/AddContactsModal';
import { AddFlowContactsModal } from '../../components/features/flows/AddFlowContactsModal';
import { UploadDatabaseModal } from '../../components/features/flows/UploadDatabaseModal';
import {
  buildTransferLineOptions,
  formatTransferNumbers,
} from '../../utils/flows/formatTransferNumbers';
import { EngageTableHeader } from '../../components/features/flows/EngageTableHeader';
import { FlowDescriptionCard } from '../../components/features/flows/FlowDescriptionCard';

interface CreatedFlow {
  id: string;
  name: string;
  scheduling_window?: number;
  schedule_over_unit?: string;
  allowed_days: string[];
  allowed_hours?: {
    start_hour: number;
    end_hour: number;
  };
  exclude_holidays: boolean;
  useRoundRobin?: boolean;
  transfer_numbers: Record<string, string>;
  scheduled_for?: string;
  description?: string;
  sub_type?: string;
  flow_type: string;
}

// Valid flow statuses
const validStatuses = ['draft', 'scheduled', 'finished', 'active', 'paused'];

const EngageFlowPage: React.FC = () => {
  const { showToast } = useToast();
  const { organization } = useOrganizationStore();
  const { users } = useOrganizationUsers();

  const hasIntegration = useHasIntegration();

  // Modal states
  const [showAddContactsModal, setShowAddContactsModal] = useState(false);
  const [showViewContactsModal, setShowViewContactsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showNewFlowModal, setShowNewFlowModal] = useState(false);
  const { isAdmin } = useUser();
  const [showCallRecordsModal, setShowCallRecordsModal] = useState(false);
  const [showAddFlowContactsModal, setShowAddFlowContactsModal] = useState(false);
  const [showUploadDatabaseModal, setShowUploadDatabaseModal] = useState(false);

  // Flow states
  const [selectedFlowId, setSelectedFlowId] = useState<string>('');
  const [createdFlow, setCreatedFlow] = useState<CreatedFlow | null>(null);
  const [flowToDelete, setFlowToDelete] = useState<Flow | null>(null);
  const [flowToEdit, setFlowToEdit] = useState<Flow | null>(null);
  const [selectedCallFlow, setSelectedCallFlow] = useState<Flow | null>(null);
  const [selectedAddFlow, setSelectedAddFlow] = useState<Flow | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreatingFlow, setIsCreatingFlow] = useState(false);
  const [newFlowStep, setNewFlowStep] = useState(0);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(25);
  const [sortConfig, setSortConfig] = useState<{ field?: string; direction: 'asc' | 'desc' }>({
    field: 'scheduledFor',
    direction: 'desc',
  });
  const [activeFilters, setActiveFilters] = useState<{
    status: string[];
    subType: string[];
    dateRange: { startDate: Date | null; endDate: Date | null };
  }>({
    status: [],
    subType: [],
    dateRange: { startDate: null, endDate: null },
  });

  // Flow basic info states
  const [flowName, setFlowName] = useState('');
  const [flowSource, setFlowSource] = useState('');
  const [selectedDescription, setSelectedDescription] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [transferLines, setTransferLines] = useState<string[]>([]);

  // Basic info errors
  const [flowNameError, setFlowNameError] = useState('');
  const [flowSourceError, setFlowSourceError] = useState('');
  const [descriptionError, setDescriptionError] = useState('');

  // Schedule settings
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [preferredTime, setPreferredTime] = useState<string | null>(null);
  const [schedulingValue, setSchedulingValue] = useState<number>(0);
  const [schedulingUnit, setSchedulingUnit] = useState<string>('');
  const [allowedDays, setAllowedDays] = useState<string[]>([]);
  const [allowedStartTime, setAllowedStartTime] = useState<string | null>(null);
  const [allowedEndTime, setAllowedEndTime] = useState<string | null>(null);
  const [excludeHolidays, setExcludeHolidays] = useState(false);
  const [useRoundRobin, setUseRoundRobin] = useState(false);
  const [flowTimezone, setFlowTimezone] = useState<string>(organization?.timezone || 'UTC');
  const [flowCountry, setFlowCountry] = useState<string>('US');

  // Schedule errors
  const [startDateError, setStartDateError] = useState('');
  const [preferredTimeError, setPreferredTimeError] = useState('');
  const [schedulingError, setSchedulingError] = useState('');
  const [allowedDaysError, setAllowedDaysError] = useState('');

  // Query hook
  const {
    data,
    totalCount,
    currentPage,
    onNextPage,
    onPrevPage,
    onPageChange,
    isLoading: loading,
    error: queryError,
    refetch: originalRefetch,
  } = useFlowsGQL({
    flowType: 'Engage',
    organizationId: organization?.id || '',
    pageSize,
    sortField: sortConfig.field,
    sortDirection: sortConfig.direction,
    searchQuery,
    filters: activeFilters,
  });

  const flows = data?.pages[0]?.flows || [];

  // Handlers
  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleFilter = useCallback(
    (filters: {
      status: string[];
      subType: string[];
      dateRange: { startDate: Date | null; endDate: Date | null };
    }) => {
      setActiveFilters(filters);
    },
    []
  );

  const handleSort = useCallback((field: string) => {
    setSortConfig((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const handleViewContacts = useCallback((flow: Flow) => {
    if (!validStatuses.includes(flow.status.toLowerCase())) return;
    setSelectedFlowId(flow.id);
    setShowViewContactsModal(true);
  }, []);

  const handleViewCallRecords = useCallback((flow: Flow) => {
    if (!validStatuses.includes(flow.status.toLowerCase())) return;
    setSelectedCallFlow(flow);
    setShowCallRecordsModal(true);
  }, []);

  const handleAddContacts = useCallback((flow: Flow) => {
    if (!validStatuses.includes(flow.status.toLowerCase())) return;
    setSelectedAddFlow(flow);
    setShowAddFlowContactsModal(true);
  }, []);

  const handleDeleteClick = useCallback((flow: Flow) => {
    if (!validStatuses.includes(flow.status.toLowerCase())) return;
    setFlowToDelete(flow);
    setShowDeleteModal(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!flowToDelete) return;

    setIsDeleting(true);
    try {
      await deleteFlow(organization?.id || '', flowToDelete.id);
      await originalRefetch();
      showToast({
        message: 'Flow archived successfully',
        variant: 'success',
      });
    } catch (error) {
      console.error('Error archiving flow:', error);
      showToast({
        message: error instanceof Error ? error.message : 'Failed to archive flow',
        variant: 'error',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setFlowToDelete(null);
    }
  }, [flowToDelete, originalRefetch, showToast]);

  const error = queryError ? (queryError as Error).message : null;

  const columns = [
    {
      key: 'name',
      header: 'Name',
      width: '300px',
      resizable: true,
      sortable: true,
      isPinned: true,
      pinPosition: 'left',
      cell: (row: Flow) => {
        const isValidStatus = validStatuses.includes(row.status.toLowerCase());
        return <div className={`${!isValidStatus ? 'opacity-50' : ''}`}>{row.name}</div>;
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '100px',
      sortable: true,
      cell: (row: Flow) => {
        const status = capitalizeFirst(row.status);
        const style = getFlowStatusStyle(status);
        const isValidStatus = validStatuses.includes(row.status.toLowerCase());
        return (
          <div className={`${!isValidStatus ? 'opacity-50' : ''}`}>
            <Badge className={`${style.background} ${style.text} font-medium`}>{status}</Badge>
          </div>
        );
      },
    },
    {
      key: 'sub_type',
      header: 'Sub-type',
      width: '120px',
      sortable: true,
      cell: (row: Flow) => {
        const subType = capitalizeFirst(row.sub_type);
        const style = getFlowSubTypeStyle(subType);
        const isValidStatus = validStatuses.includes(row.status.toLowerCase());
        return (
          <div className={`${!isValidStatus ? 'opacity-50' : ''}`}>
            <Badge className={`${style.background} ${style.text} font-medium`}>{subType}</Badge>
          </div>
        );
      },
    },
    {
      key: 'scheduled_for',
      header: 'Scheduled for',
      width: '230px',
      sortable: true,
      cell: (row: Flow) => {
        const isValidStatus = validStatuses.includes(row.status.toLowerCase());
        return (
          <div className={`whitespace-nowrap ${!isValidStatus ? 'opacity-50' : ''}`}>
            {row.scheduled_for}
          </div>
        );
      },
    },
    {
      key: 'contacts',
      header: 'Contacts',
      width: '120px',
      isCentered: true,
      cell: (row: Flow) => {
        const isValidStatus = validStatuses.includes(row.status.toLowerCase());
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleViewContacts(row)}
            className={`px-3 py-1 text-xs ${!isValidStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!isValidStatus}
          >
            {formatNumber(row.contacts_count ?? 0)}
          </Button>
        );
      },
    },
    {
      key: 'calls',
      header: 'Calls',
      width: '120px',
      isCentered: true,
      cell: (row: Flow) => {
        const isValidStatus = validStatuses.includes(row.status.toLowerCase());
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleViewCallRecords(row)}
            className={`px-3 py-1 text-xs ${!isValidStatus ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={!isValidStatus}
          >
            {formatNumber(row.calls_count ?? 0)}
          </Button>
        );
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '160px',
      isCentered: true,
      isPinned: true,
      pinPosition: 'right',
      cell: (row: Flow) => {
        const isValidStatus = validStatuses.includes(row.status.toLowerCase());

        return (
          <div className="relative flex items-center justify-center gap-2">
            {/* Add Contacts */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleAddContacts(row)}
              className={`p-2 ${isValidStatus ? 'hover:bg-gray-100 dark:hover:bg-white/10' : 'opacity-50 cursor-not-allowed'}`}
              title="Add Contacts"
              disabled={!isValidStatus || row.status.toLowerCase() === 'finished'}
            >
              <UserPlus
                className={`w-3.5 h-3.5 ${isValidStatus ? 'text-gray-800 dark:text-gray-200' : 'text-gray-400'}`}
              />
            </Button>

            {/* Edit */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!isValidStatus) return;
                setFlowToEdit(row);
                setShowEditModal(true);
              }}
              className={`p-2 ${isValidStatus ? 'hover:bg-gray-100 dark:hover:bg-white/10' : 'opacity-50 cursor-not-allowed'}`}
              title="Edit"
              disabled={!isValidStatus}
            >
              <Pencil
                className={`w-3.5 h-3.5 ${isValidStatus ? 'text-gray-800 dark:text-gray-200' : 'text-gray-400'}`}
              />
            </Button>

            {/* Delete — visible for everyone, disabled for agents */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDeleteClick(row)}
              className={`p-2 ${
                isValidStatus && isAdmin
                  ? 'text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 border-red-300 dark:border-red-700/30 hover:border-red-500'
                  : 'opacity-50 cursor-not-allowed'
              }`}
              title={!isAdmin ? 'Only your organization admin can delete flows' : 'Delete'}
              disabled={
                !isAdmin ||
                !isValidStatus ||
                isDeleting ||
                !['draft', 'paused'].includes(row.status.toLowerCase())
              }
            >
              <Trash
                className={`w-3.5 h-3.5 ${isValidStatus && isAdmin ? '' : 'text-gray-400'}`}
              />
            </Button>

            {/* Loading Animation for Invalid Status */}
            {!isValidStatus && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/50 dark:bg-black/50">
                <RotateCw className="w-3.5 h-3.5 animate-spin text-primary" />
              </div>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Container className="px-4 lg:px-5 xl:pl-20 xl:pr-5 pb-6">
      <div className="flex flex-col space-y-6 lg:space-y-10">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:gap-6">
          {/* Title and Actions */}
          <div>
            <div className="flex flex-col lg:flex-row justify-between lg:justify-start items-start lg:items-center gap-3 lg:gap-20">
              <div>
                <Typography variant="h3" as="h1" className="!text-xl font-bold">
                  Engage Flows
                </Typography>
                <Typography variant="small" className="text-gray-500 dark:text-gray-400">
                  {formatNumber(totalCount)} {totalCount === 1 ? 'record' : 'records'}
                </Typography>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full lg:w-auto">
                <Button
                  variant="primary"
                  size="md"
                  className="flex items-center justify-center gap-2"
                  onClick={() => setShowNewFlowModal(true)}
                  disabled={!isAdmin}
                >
                  <PlusCircle size={16} />
                  New Engage Flow
                </Button>
                {!hasIntegration && (
                  <Button
                    variant="outline"
                    size="md"
                    className="flex items-center justify-center gap-2"
                    onClick={() => setShowUploadDatabaseModal(true)}
                    disabled={!isAdmin}
                  >
                    <Upload size={16} />
                    Upload Database
                  </Button>
                )}
                {!isAdmin && (
                  <p className="text-xs text-muted-foreground self-center">
                    For launching new flows, contact the admin on your account.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Description */}
          <FlowDescriptionCard
            flowType="engage"
            description={[
              'Engage Flows are your one-shot megaphone to the database: Remi makes a single, personalized call to share whatever matters—event invites, giveaways, open house details, market nuggets, quick check-ins—and logs every response.',
              "It's the simplest way to stay top of mind at scale, without sounding like a blast.",
            ]}
            icon={Megaphone}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col space-y-2">
          <EngageTableHeader
            searchQuery={searchQuery}
            onSearchChange={handleSearch}
            onRefresh={async () => {
              try {
                // Reset search and filters
                setSearchQuery('');
                setActiveFilters({
                  status: [],
                  subType: [],
                  dateRange: { startDate: null, endDate: null },
                });
                await originalRefetch();
              } catch (error) {
                console.error('Error refreshing flows:', error);
                showToast({
                  message: 'Failed to refresh flows',
                  variant: 'error',
                });
              }
            }}
            onFilter={handleFilter}
            isLoading={loading}
            totalFlows={totalCount}
          />
          <TableCard
            className="h-[60vh] sm:h-[calc(100vh-220px)]"
            footer={
              <OffsetPagination
                currentPage={currentPage}
                totalItems={totalCount}
                pageSize={pageSize}
                onNextPage={onNextPage}
                onPrevPage={onPrevPage}
                onPageChange={onPageChange}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                }}
                isLoading={loading}
                label="flows"
              />
            }
          >
            <Table<Flow>
              columns={columns}
              data={flows}
              keyExtractor={(item) => item.id}
              sortable={true}
              onSort={handleSort}
              loading={loading}
              emptyStateText="No engage flows available"
            />
          </TableCard>
          {/* Delete Confirmation Dialog */}
          <ConfirmationDialog
            isOpen={showDeleteModal}
            onClose={() => {
              if (!isDeleting) {
                setShowDeleteModal(false);
                setFlowToDelete(null);
              }
            }}
            onConfirm={handleConfirmDelete}
            title="Delete Flow"
            message={
              flowToDelete
                ? `Are you sure you want to delete the flow "${flowToDelete.name}"?`
                : 'Are you sure you want to delete this flow?'
            }
            confirmLabel="Delete"
            isLoading={isDeleting}
            variant="danger"
          />
          {/* Edit Flow Modal */}
          <EditFlowModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setFlowToEdit(null);
            }}
            flow={flowToEdit}
            onSuccess={async () => {
              await originalRefetch();
            }}
          />
          {/* Call Records Modal */}
          <FlowCallRecordsModal
            isOpen={showCallRecordsModal}
            onClose={() => {
              setShowCallRecordsModal(false);
              setSelectedCallFlow(null);
            }}
            flowId={selectedCallFlow?.id || ''}
            flowName={selectedCallFlow?.name || ''}
            orgTimezone={organization?.timezone}
            organization={organization || undefined}
          />
          {/* View Contacts Modal */}
          <ViewFlowContactsModal
            isOpen={showViewContactsModal}
            onClose={() => {
              setShowViewContactsModal(false);
              setSelectedFlowId('');
            }}
            flowId={selectedFlowId}
          />
          {/* New Flow Stepped Modal */}
          <SteppedModal
            isOpen={showNewFlowModal}
            onClose={() => {
              setShowNewFlowModal(false);
              // Only reset errors and step, keep the values
              setFlowNameError('');
              setFlowSourceError('');
              setDescriptionError('');
              setNewFlowStep(0);
            }}
            headerTitle="New Engage Flow"
            title={
              newFlowStep === 0
                ? 'What type of Engage flow is this?'
                : newFlowStep === 1
                  ? 'Describe your Engage flow'
                  : newFlowStep === 2
                    ? 'Select transfer line'
                    : newFlowStep === 3
                      ? 'Schedule your Engage flow'
                      : 'Engage flow summary'
            }
            currentStep={newFlowStep}
            onNext={() => setNewFlowStep((prev) => prev + 1)}
            disableNext={
              (newFlowStep === 0 && (!flowName.trim() || !flowSource.trim())) ||
              (newFlowStep === 1 && !customDescription.trim()) ||
              (newFlowStep === 2 && (!transferLines || transferLines.length === 0)) ||
              (newFlowStep === 3 &&
                (!startDate ||
                  !preferredTime ||
                  !schedulingValue ||
                  !schedulingUnit ||
                  !allowedDays.length))
            }
            onPrevious={() => setNewFlowStep((prev) => prev - 1)}
            onReset={() => {
              // Reset basic info
              setFlowName('');
              setFlowSource('');
              setSelectedDescription('');
              setCustomDescription('');
              setTransferLines([]);

              // Reset schedule settings
              setStartDate(null);
              setPreferredTime(null);
              setSchedulingValue(0);
              setSchedulingUnit('');
              setAllowedDays([]);
              setAllowedStartTime(null);
              setAllowedEndTime(null);
              setExcludeHolidays(false);
              setUseRoundRobin(false);
              setFlowTimezone(organization?.timezone || 'UTC');
              setFlowCountry('US');

              // Reset all errors
              setFlowNameError('');
              setFlowSourceError('');
              setDescriptionError('');
              setStartDateError('');
              setPreferredTimeError('');
              setSchedulingError('');
              setAllowedDaysError('');

              // Reset step
              setNewFlowStep(0);
            }}
            onComplete={async () => {
              setIsCreatingFlow(true);
              try {
                // Get selected transfer line options and format them
                const selectedTransferOptions = buildTransferLineOptions(users).filter((opt) =>
                  transferLines.includes(opt.value)
                );
                const formattedNumbers = formatTransferNumbers(selectedTransferOptions);

                // Get first transfer number for call_settings
                const transferPhoneNumber = selectedTransferOptions[0]?.value || null;

                // Convert time strings to hours
                const startHour = allowedStartTime ? parseInt(allowedStartTime.split(':')[0]) : 9;
                const endHour = allowedEndTime ? parseInt(allowedEndTime.split(':')[0]) : 18;

                // Generate voicemail content first so we can include it in
                // the createFlow payload — avoids a follow-up PUT that would
                // null out other fields.
                let voicemailContent: string | undefined;
                try {
                  const vmResponse = await generateVoicemailAPI(customDescription);
                  voicemailContent = vmResponse.voicemail;
                } catch (vmError) {
                  console.warn('Voicemail generation failed:', vmError);
                }

                // Create flow and get response
                const response = await createFlow({
                  name: flowName,
                  description: customDescription,
                  description_id: selectedDescription,
                  flow_type: 'Engage',
                  sub_type: flowSource,
                  timezone: flowTimezone,
                  country_code: flowCountry,
                  scheduled_for:
                    startDate && preferredTime
                      ? moment
                          .tz(
                            `${moment(startDate).format('YYYY-MM-DD')} ${preferredTime}`,
                            'YYYY-MM-DD HH:mm',
                            flowTimezone
                          )
                          .format()
                      : '',
                  scheduling_window: schedulingValue,
                  schedule_over_unit: schedulingUnit,
                  allowed_days: allowedDays,
                  allowed_hours: {
                    start_hour: startHour,
                    end_hour: endHour,
                  },
                  exclude_holidays: excludeHolidays,
                  useRoundRobin: useRoundRobin,
                  transfer_numbers: formattedNumbers,
                  voicemail_content: voicemailContent,
                });

                // Refresh flows list and show add contacts modal
                await originalRefetch();
                showToast({
                  message: 'Flow created successfully',
                  variant: 'success',
                });

                // Store flow details and show add contacts modal
                setCreatedFlow({
                  id: response.flowId,
                  name: flowName,
                  scheduling_window: schedulingValue,
                  schedule_over_unit: schedulingUnit,
                  allowed_days: allowedDays,
                  allowed_hours: {
                    start_hour: allowedStartTime ? parseInt(allowedStartTime.split(':')[0]) : 9,
                    end_hour: allowedEndTime ? parseInt(allowedEndTime.split(':')[0]) : 18,
                  },
                  exclude_holidays: excludeHolidays,
                  useRoundRobin: useRoundRobin,
                  transfer_numbers: formattedNumbers,
                  scheduled_for:
                    startDate && preferredTime
                      ? moment
                          .tz(
                            `${moment(startDate).format('YYYY-MM-DD')} ${preferredTime}`,
                            'YYYY-MM-DD HH:mm',
                            organization?.timezone || 'UTC'
                          )
                          .format()
                      : undefined,
                  description: customDescription,
                  sub_type: flowSource,
                  flow_type: 'Engage',
                });

                setShowAddContactsModal(true);

                // Reset all form values
                setFlowName('');
                setFlowSource('');
                setSelectedDescription('');
                setCustomDescription('');
                setTransferLines([]);
                setStartDate(null);
                setPreferredTime(null);
                setSchedulingValue(0);
                setSchedulingUnit('');
                setAllowedDays([]);
                setAllowedStartTime(null);
                setAllowedEndTime(null);
                setExcludeHolidays(false);

                // Reset step and close flow modal
                setNewFlowStep(0);
                setShowNewFlowModal(false);
              } catch (error) {
                console.error('Error creating flow:', error);
                showToast({
                  message: error instanceof Error ? error.message : 'Failed to create flow',
                  variant: 'error',
                });
              } finally {
                setIsCreatingFlow(false);
              }
            }}
            size="xl"
          >
            {[0, 1, 2, 3, 4].map((stepIndex) => (
              <div key={stepIndex}>
                <EngageFlowSteps
                  step={stepIndex}
                  // Flow basic info
                  flowName={flowName}
                  flowSource={flowSource}
                  selectedDescription={selectedDescription}
                  customDescription={customDescription}
                  transferLines={transferLines}
                  // Schedule settings
                  startDate={startDate}
                  preferredTime={preferredTime}
                  schedulingValue={schedulingValue}
                  schedulingUnit={schedulingUnit}
                  allowedDays={allowedDays}
                  allowedStartTime={allowedStartTime}
                  allowedEndTime={allowedEndTime}
                  excludeHolidays={excludeHolidays}
                  useRoundRobin={useRoundRobin}
                  timezone={flowTimezone}
                  country={flowCountry}
                  // Errors
                  flowNameError={flowNameError}
                  flowSourceError={flowSourceError}
                  descriptionError={descriptionError}
                  startDateError={startDateError}
                  preferredTimeError={preferredTimeError}
                  schedulingError={schedulingError}
                  allowedDaysError={allowedDaysError}
                  // Basic info handlers
                  onFlowNameChange={(value) => {
                    setFlowName(value);
                    if (flowNameError) setFlowNameError('');
                  }}
                  onFlowSourceChange={(value) => {
                    setFlowSource(value);
                    if (flowSourceError) setFlowSourceError('');
                  }}
                  onSelectedDescriptionChange={setSelectedDescription}
                  onCustomDescriptionChange={(value) => {
                    setCustomDescription(value);
                    if (descriptionError) setDescriptionError('');
                  }}
                  onTransferLinesChange={setTransferLines}
                  // Schedule handlers
                  onStartDateChange={setStartDate}
                  onPreferredTimeChange={setPreferredTime}
                  onSchedulingValueChange={(value) => setSchedulingValue(Number(value))}
                  onSchedulingUnitChange={setSchedulingUnit}
                  onAllowedDaysChange={setAllowedDays}
                  onAllowedStartTimeChange={setAllowedStartTime}
                  onAllowedEndTimeChange={setAllowedEndTime}
                  onExcludeHolidaysChange={setExcludeHolidays}
                  onUseRoundRobinChange={setUseRoundRobin}
                  onTimezoneChange={setFlowTimezone}
                  onCountryChange={setFlowCountry}
                />
              </div>
            ))}
          </SteppedModal>
          {/* Loading Dialog */}
          <LoadingDialog isOpen={isCreatingFlow} message="Flow is building..." />
          {/* Add Flow Contacts Modal */}
          <AddFlowContactsModal
            isOpen={showAddFlowContactsModal}
            onClose={() => {
              setShowAddFlowContactsModal(false);
              setSelectedAddFlow(null);
            }}
            flowId={selectedAddFlow?.id || ''}
            flowName={selectedAddFlow?.name || ''}
            onSuccess={async () => {
              await originalRefetch();
            }}
          />
          {/* Add Contacts Modal */}
          <AddContactsModal
            isOpen={showAddContactsModal}
            onClose={async () => {
              setShowAddContactsModal(false);
              const newFlowId = createdFlow?.id;
              setCreatedFlow(null);
              if (!newFlowId) return;
              // Refetch to pick up contact count and status change
              // (draft → scheduled) from ScheduleFlowModal. Use the
              // returned flows directly to avoid stale closure data.
              const freshFlows = await originalRefetch();
              const flowFromList = (freshFlows as Flow[]).find((f) => f.id === newFlowId);
              if (flowFromList) {
                setFlowToEdit(flowFromList);
                setShowEditModal(true);
              }
            }}
            flowName={createdFlow?.name ?? ''}
            flowId={createdFlow?.id ?? ''}
            schedulingWindow={createdFlow?.scheduling_window}
            scheduleOverUnit={createdFlow?.schedule_over_unit}
            allowedDays={createdFlow?.allowed_days ?? []}
            allowedHours={createdFlow?.allowed_hours}
            excludeHolidays={createdFlow?.exclude_holidays ?? false}
            transferNumbers={createdFlow?.transfer_numbers ?? {}}
            timezone={organization?.timezone}
            scheduledFor={createdFlow?.scheduled_for}
            description={createdFlow?.description}
            subType={createdFlow?.sub_type}
          />
          {/* Upload Database Modal */}
          <UploadDatabaseModal
            isOpen={showUploadDatabaseModal}
            onClose={() => setShowUploadDatabaseModal(false)}
          />
        </div>
      </div>
    </Container>
  );
};

export default EngageFlowPage;
